import { useMemo, useState } from 'react';

type Json = Record<string, any>;

type GlassBoxStatus = 'ui-only' | 'wired' | 'mismatch' | 'missing';
type GlassBoxTab = 'ui' | 'normalized' | 'engineInput' | 'trace' | 'output';

interface TraceEvent {
  stage: string;
  type: string;
  field?: string;
  inputs?: any;
  output?: any;
  notes?: string;
}

interface GlassBoxBundle {
  uiSnapshot: Json;
  normalizedSnapshot: Json;
  engineInputSnapshot: Json;
  outputSnapshot: Json;
  traceEvents: TraceEvent[];
}

const STORAGE_KEY = 'glassBoxBundles';

const STUB_BUNDLE: GlassBoxBundle = {
  uiSnapshot: {
    propertyType: 'semi_detached',
    bedrooms: 3,
    bathrooms: 2,
    gcNumber: '47-116-30',
    hotWaterDemand: 'medium',
    mainsStaticPressureBar: 2.6,
    mainsFlowLpm: 14,
    insulationLevel: 'average',
    emitterLowTempSuitability: 'likely_ok',
    lengthOfStay: '5-10y',
    sludgeRisk: 'possible',
  },
  normalizedSnapshot: {
    propertyType: 'semiDetached',
    bedrooms: 3,
    bathrooms: 2,
    boilerEfficiency: { valuePercent: 88, source: 'gc_lookup' },
    gcNumber: '47-116-30',
    hotWaterDemand: 'medium',
    mainsStaticPressureBar: 2.6,
    mainsFlowLpm: 14,
    insulationLevel: 'average',
    emitterLowTempSuitability: 'likely_ok',
    lengthOfStay: '5-10y',
    sludgeRisk: 'possible',
  },
  engineInputSnapshot: {
    propertyType: 'semiDetached',
    bedrooms: 3,
    bathrooms: 2,
    hotWaterDemand: 'medium',
    boilerEfficiencyPercent: 88,
    mainsStaticPressureBar: 2.5,
    mainsFlowLpm: 14,
    insulationLevel: 'average',
    emitterLowTempSuitability: 'likely_ok',
    lengthOfStay: '5-10y',
    sludgeRisk: 'possible',
  },
  outputSnapshot: {
    recommendedOptionId: 'A',
    scores: { hp: 0.72, boiler: 0.58 },
    notes: ['Stubbed output for UI walkthrough'],
  },
  traceEvents: [
    { stage: 'normalize', type: 'MAP', field: 'propertyType', notes: 'semi_detached → semiDetached' },
    { stage: 'normalize', type: 'MAP', field: 'boilerEfficiencyPercent', notes: 'Inferred from GC number', output: 88 },
    { stage: 'enrich', type: 'WARNING', field: 'mainsStaticPressureBar', notes: 'Rounded to nearest 0.1 for model constraints' },
    { stage: 'engine', type: 'SCORE', field: 'recommendedOptionId', output: 'A', notes: 'HP score higher than boiler score' },
  ],
};

const FIELD_KEYS = [
  'propertyType',
  'propertyAgeBand',
  'bedrooms',
  'bathrooms',
  'hotWaterDemand',
  'gcNumber',
  'boilerMake',
  'boilerModel',
  'mainsStaticPressureBar',
  'mainsFlowLpm',
  'insulationLevel',
  'emitterLowTempSuitability',
  'lengthOfStay',
  'sludgeRisk',
];

function readJsonStorage(key: string): any {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key) ?? localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getGlassBoxBundle(doc: any, optionId: string): GlassBoxBundle | null {
  if (!doc || typeof doc !== 'object') return null;

  return (
    doc?.debug?.glassBox?.[optionId] ??
    doc?.debugBundles?.[optionId] ??
    doc?.glassBox?.[optionId] ??
    doc?.payload?.debug?.glassBox?.[optionId] ??
    null
  );
}

function valuesEqualish(a: any, b: any): boolean {
  if (a === b) return true;
  if ((a === null || a === undefined || a === '') && (b === null || b === undefined || b === '')) return true;

  const numA = Number(a);
  const numB = Number(b);
  if (Number.isFinite(numA) && Number.isFinite(numB)) {
    return Math.abs(numA - numB) < 0.11;
  }

  return String(a).toLowerCase() === String(b).toLowerCase();
}

function computeStatus(uiValue: any, normValue: any, engineValue: any): GlassBoxStatus {
  const hasUi = uiValue !== undefined && uiValue !== null && uiValue !== '';
  const hasNorm = normValue !== undefined && normValue !== null && normValue !== '';
  const hasEngine = engineValue !== undefined && engineValue !== null && engineValue !== '';

  if (!hasUi && !hasNorm && !hasEngine) return 'missing';
  if (hasUi && !hasNorm && !hasEngine) return 'ui-only';
  if (hasUi && hasNorm && hasEngine && valuesEqualish(uiValue, normValue) && valuesEqualish(normValue, engineValue)) {
    return 'wired';
  }
  return 'mismatch';
}

function statusColor(status: GlassBoxStatus): string {
  if (status === 'wired') return '#166534';
  if (status === 'ui-only') return '#1d4ed8';
  if (status === 'mismatch') return '#b45309';
  return '#6b7280';
}

export default function GlassBoxPage() {
  const [activeOptionId, setActiveOptionId] = useState<'A' | 'B' | 'C'>('A');
  const [tab, setTab] = useState<GlassBoxTab>('ui');
  const [selectedField, setSelectedField] = useState<string>('propertyType');
  const [showMismatchesOnly, setShowMismatchesOnly] = useState(false);
  const [showUiOnlyOnly, setShowUiOnlyOnly] = useState(false);
  const [showWarningsOnly, setShowWarningsOnly] = useState(false);

  const liveDoc = useMemo(() => {
    const sessionStory = readJsonStorage('storySnapshot');
    const collectedData = readJsonStorage('collectedData');
    const recommendationResult = readJsonStorage('recommendationResult');
    const captured = readJsonStorage(STORAGE_KEY);

    return {
      ...sessionStory,
      rawInput: sessionStory?.rawInput ?? collectedData,
      recommendationResult,
      debug: {
        ...(sessionStory?.debug ?? {}),
        glassBox: captured ?? sessionStory?.debug?.glassBox,
      },
    };
  }, []);

  const realBundle = useMemo(() => getGlassBoxBundle(liveDoc, activeOptionId), [liveDoc, activeOptionId]);

  const bundle: GlassBoxBundle = realBundle ?? STUB_BUNDLE;

  const matrixRows = useMemo(() => {
    return FIELD_KEYS.map((key) => {
      const uiValue = bundle.uiSnapshot?.[key] ?? '';
      const normValue = bundle.normalizedSnapshot?.[key] ?? '';
      const engineValue = bundle.engineInputSnapshot?.[key] ?? '';
      const status = computeStatus(uiValue, normValue, engineValue);

      return { key, uiValue, normValue, engineValue, status };
    }).filter((row) => {
      if (showMismatchesOnly && row.status !== 'mismatch') return false;
      if (showUiOnlyOnly && row.status !== 'ui-only') return false;
      return true;
    });
  }, [bundle, showMismatchesOnly, showUiOnlyOnly]);

  const filteredTrace = useMemo(() => {
    if (!showWarningsOnly) return bundle.traceEvents;
    return bundle.traceEvents.filter((event) => event.type === 'WARNING');
  }, [bundle.traceEvents, showWarningsOnly]);

  const tabContent = useMemo(() => {
    if (tab === 'ui') return bundle.uiSnapshot;
    if (tab === 'normalized') return bundle.normalizedSnapshot;
    if (tab === 'engineInput') return bundle.engineInputSnapshot;
    if (tab === 'output') return bundle.outputSnapshot;
    return bundle.traceEvents;
  }, [tab, bundle]);

  const captureNow = () => {
    if (typeof window === 'undefined') return;

    const current = readJsonStorage(STORAGE_KEY) ?? {};
    const capturedBundle: GlassBoxBundle = {
      uiSnapshot: liveDoc?.rawInput ?? bundle.uiSnapshot,
      normalizedSnapshot: liveDoc?.recommendationResult?.normalizedOutput ?? bundle.normalizedSnapshot,
      engineInputSnapshot: liveDoc?.recommendationResult?.normalizedInput ?? bundle.engineInputSnapshot,
      outputSnapshot: liveDoc?.recommendationResult?.output ?? bundle.outputSnapshot,
      traceEvents: bundle.traceEvents,
    };

    const next = {
      ...current,
      [activeOptionId]: capturedBundle,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  return (
    <div style={{ padding: 16 }}>
      <h2>Glass Box (v1)</h2>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <strong>Option</strong>
        {(['A', 'B', 'C'] as const).map((id) => (
          <button key={id} onClick={() => setActiveOptionId(id)} style={{ fontWeight: activeOptionId === id ? 700 : 400 }}>
            {id}
          </button>
        ))}

        {([
          ['ui', 'UI'],
          ['normalized', 'Normalized'],
          ['engineInput', 'Engine Input'],
          ['trace', 'Trace'],
          ['output', 'Output'],
        ] as Array<[GlassBoxTab, string]>).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ fontWeight: tab === id ? 700 : 400 }}>
            {label}
          </button>
        ))}

        <button onClick={captureNow}>Capture current run → Glass Box</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 40%) 1fr', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <label><input type="checkbox" checked={showMismatchesOnly} onChange={(e) => setShowMismatchesOnly(e.target.checked)} /> mismatches only</label>
            <label><input type="checkbox" checked={showUiOnlyOnly} onChange={(e) => setShowUiOnlyOnly(e.target.checked)} /> ui-only only</label>
            <label><input type="checkbox" checked={showWarningsOnly} onChange={(e) => setShowWarningsOnly(e.target.checked)} /> show warnings only</label>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th align="left">Field</th>
                <th align="left">Status</th>
              </tr>
            </thead>
            <tbody>
              {matrixRows.map((row) => (
                <tr key={row.key} onClick={() => setSelectedField(row.key)} style={{ cursor: 'pointer', background: selectedField === row.key ? '#f3f4f6' : undefined }}>
                  <td>{row.key}</td>
                  <td>
                    <span style={{ color: '#fff', background: statusColor(row.status), borderRadius: 4, padding: '2px 6px', fontSize: 12 }}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedField && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div><strong>UI:</strong> {String(bundle.uiSnapshot?.[selectedField] ?? '')}</div>
              <div><strong>Normalized:</strong> {String(bundle.normalizedSnapshot?.[selectedField] ?? '')}</div>
              <div><strong>Engine:</strong> {String(bundle.engineInputSnapshot?.[selectedField] ?? '')}</div>
            </div>
          )}
        </div>

        <div>
          {tab === 'trace' ? (
            <div>
              {['normalize', 'enrich', 'engine'].map((stage) => {
                const stageEvents = filteredTrace.filter((event) => event.stage === stage);
                if (!stageEvents.length) return null;

                return (
                  <details key={stage} open>
                    <summary style={{ fontWeight: 600 }}>{stage}</summary>
                    {stageEvents.map((event, index) => (
                      <details key={`${event.type}-${event.field ?? 'x'}-${index}`} style={{ marginLeft: 12 }}>
                        <summary>{event.type} {event.field ? `• ${event.field}` : ''} {event.notes ? `• ${event.notes}` : ''}</summary>
                        <pre>{JSON.stringify({ inputs: event.inputs, output: event.output }, null, 2)}</pre>
                      </details>
                    ))}
                  </details>
                );
              })}
            </div>
          ) : (
            <pre style={{ margin: 0, padding: 12, background: '#111827', color: '#e5e7eb', minHeight: 440, overflow: 'auto' }}>
              {JSON.stringify(tabContent, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
